import React, { useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import { processService } from '../../services/api';

const ProjectConfigTab = ({ projectId, onClose }) => {
  const { deleteProject, projects, loadProjects } = useProject();
  const [documents, setDocuments] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [projectConfig, setProjectConfig] = useState({
    name: '',
    description: '',
    client: '',
    baName: '',
    saName: '',
    developers: ''
  });
  const [saveStatus, setSaveStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteProcessModal, setShowDeleteProcessModal] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  const [showAddProcessDisclaimerModal, setShowAddProcessDisclaimerModal] = useState(false);
  const [isProjectDetailsExpanded, setIsProjectDetailsExpanded] = useState(false);
  const [isProcessesExpanded, setIsProcessesExpanded] = useState(false);
  const [isDocumentsExpanded, setIsDocumentsExpanded] = useState(false);

  // Load project data when component mounts or projectId changes
  React.useEffect(() => {
    if (!projectId) return;
    
    const loadProjectData = async () => {
      try {
        const response = await fetch(`/api/projects/${projectId}`);
        const data = await response.json();
        
        if (data) {
          setProjectConfig({
            name: data.name || '',
            description: data.description || '',
            client: data.client || '',
            baName: data.baName || '',
            saName: data.saName || '',
            developers: data.developers || ''
          });
        }

        // Load processes for this project
        const processesResponse = await processService.getProcessesByProject(projectId);
        setProcesses(processesResponse.data || []);
      } catch (error) {
        console.error('Error loading project data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [projectId]);

  const handleInputChange = (field, value) => {
    setProjectConfig(prev => ({ ...prev, [field]: value }));
  };

  const addDocument = () => {
    const newDoc = {
      id: Date.now(),
      type: 'link',
      url: '',
      description: '',
      isEditing: true
    };
    setDocuments(prev => [...prev, newDoc]);
  };

  const saveDocument = (docId, docData) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, ...docData, isEditing: false } : doc
    ));
  };

  const removeDocument = (docId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId));
  };

  const editDocument = (docId) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, isEditing: true } : doc
    ));
  };

  const cancelEdit = (docId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId || !doc.isEditing));
  };

  const handleDeleteProcessClick = (process) => {
    setProcessToDelete(process);
    setShowDeleteProcessModal(true);
  };

  const handleConfirmDeleteProcess = async () => {
    if (!processToDelete) return;

    try {
      if (!processToDelete.isNew) {
        await processService.deleteProcess(processToDelete.id);
      }
      setProcesses(prev => prev.filter(p => p.id !== processToDelete.id));
      setShowDeleteProcessModal(false);
      setProcessToDelete(null);
      
      // Notify that processes have changed by dispatching a custom event
      window.dispatchEvent(new CustomEvent('processesChanged', { detail: { projectId } }));
    } catch (error) {
      console.error('Error deleting process:', error);
      alert('Failed to delete process: ' + error.message);
      setShowDeleteProcessModal(false);
      setProcessToDelete(null);
    }
  };

  const handleCancelDeleteProcess = () => {
    setShowDeleteProcessModal(false);
    setProcessToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectId) {
      setSaveStatus('❌ No project selected');
      setTimeout(() => setSaveStatus(''), 2500);
      return;
    }

    // Validate project name
    if (!projectConfig.name.trim()) {
      setSaveStatus('❌ Project name is required');
      setTimeout(() => setSaveStatus(''), 2500);
      return;
    }

    // Check for duplicate project name (excluding current project)
    const duplicateProject = projects.find(
      p => p.id !== projectId && p.name.toLowerCase() === projectConfig.name.trim().toLowerCase()
    );
    
    if (duplicateProject) {
      setSaveStatus('❌ Project name already exists');
      setTimeout(() => setSaveStatus(''), 2500);
      return;
    }

    setSaving(true);
    setSaveStatus('Saving...');

    try {
      // Save project configuration
      const response = await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectConfig)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save configuration');
      }

      const updatedProject = await response.json();
      
      // Update local state with saved data
      setProjectConfig({
        name: updatedProject.name || '',
        description: updatedProject.description || '',
        client: updatedProject.client || '',
        baName: updatedProject.baName || '',
        saName: updatedProject.saName || '',
        developers: updatedProject.developers || '',
        repositoryUrl: updatedProject.repositoryUrl || ''
      });

      // Reload projects list to update sidebar
      await loadProjects();

      setSaveStatus('✅ Saved successfully');
      
      // Close the configuration modal after successful save
      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 500); // Brief delay to show success message
      } else {
        setTimeout(() => setSaveStatus(''), 2500);
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveStatus(`❌ ${error.message}`);
      setTimeout(() => setSaveStatus(''), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!projectId) return;

    try {
      // Reload project data from backend
      const response = await fetch(`/api/projects/${projectId}`);
      const data = await response.json();
      
      if (data) {
        setProjectConfig({
          name: data.name || '',
          description: data.description || '',
          client: data.client || '',
          baName: data.baName || '',
          saName: data.saName || '',
          developers: data.developers || ''
        });
      }

      setDocuments([]);
      setSaveStatus('✅ Reset to saved values');
      setTimeout(() => setSaveStatus(''), 1500);
    } catch (error) {
      console.error('Error resetting configuration:', error);
      setSaveStatus('❌ Failed to reset');
      setTimeout(() => setSaveStatus(''), 1500);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectId) return;

    setDeleting(true);
    try {
      await deleteProject(projectId);
      // Context will handle navigation
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Project Details Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div 
            className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 cursor-pointer"
            onClick={() => setIsProjectDetailsExpanded(!isProjectDetailsExpanded)}
          >
            <div className="flex items-center">
              <div className="w-1 h-6 bg-blue-500 rounded mr-3"></div>
              <h3 className="text-lg font-semibold text-gray-800">Project Details</h3>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-500 transition-transform ${isProjectDetailsExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isProjectDetailsExpanded && (
          <div className="space-y-4">
            <div>
              <label htmlFor="project-name" className="block text-gray-700 text-sm font-semibold mb-2">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="project-name"
                value={projectConfig.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter project name"
                required
              />
            </div>

            <div>
              <label htmlFor="project-desc" className="block text-gray-700 text-sm font-semibold mb-2">
                Project Description
              </label>
              <textarea
                id="project-desc"
                rows="3"
                value={projectConfig.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="A brief description of this project..."
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="client" className="block text-gray-700 text-sm font-semibold mb-2">
                  Client
                </label>
                <input
                  type="text"
                  id="client"
                  value={projectConfig.client}
                  onChange={(e) => handleInputChange('client', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Client Name"
                />
              </div>
              
              <div>
                <label htmlFor="ba-name" className="block text-gray-700 text-sm font-semibold mb-2">
                  Business Analyst
                </label>
                <input
                  type="text"
                  id="ba-name"
                  value={projectConfig.baName}
                  onChange={(e) => handleInputChange('baName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="BA's Name"
                />
              </div>
              
              <div>
                <label htmlFor="sa-name" className="block text-gray-700 text-sm font-semibold mb-2">
                  Solution Architect
                </label>
                <input
                  type="text"
                  id="sa-name"
                  value={projectConfig.saName}
                  onChange={(e) => handleInputChange('saName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="SA's Name"
                />
              </div>
              
              <div>
                <label htmlFor="developers" className="block text-gray-700 text-sm font-semibold mb-2">
                  Developers (comma-separated)
                </label>
                <input
                  type="text"
                  id="developers"
                  value={projectConfig.developers}
                  onChange={(e) => handleInputChange('developers', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Dev 1, Dev 2, ..."
                />
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Processes Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div 
            className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 cursor-pointer"
            onClick={() => setIsProcessesExpanded(!isProcessesExpanded)}
          >
            <div className="flex items-center">
              <div className="w-1 h-6 bg-green-500 rounded mr-3"></div>
              <h3 className="text-lg font-semibold text-gray-800">Project Code (Processes)</h3>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-500 transition-transform ${isProcessesExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isProcessesExpanded && (
          <>
          <div className="flex items-center justify-between mb-4">
            <div />
            <button 
              type="button" 
              onClick={(e) => {
                e.stopPropagation();
                setShowAddProcessDisclaimerModal(true);
              }}
              className="btn-secondary text-xs px-4 py-2"
            >
              + Add Process
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mb-4">
            Add one or more processes (repositories) to this project. Each process represents an independent codebase, such as a Dispatcher or Performer.
          </p>
          
          {processes.length > 0 ? (
            <div className="space-y-4">
              {processes.map((process, index) => (
                <ProcessItem 
                  key={process.id}
                  process={process}
                  projectId={projectId}
                  onUpdate={async (updatedProcess, uploadFile) => {
                    if (updatedProcess.isNew) {
                      // Create new process
                      try {
                        // For manual uploads, validate the file FIRST before creating the process
                        if (updatedProcess.sourceType === 'manual' && uploadFile) {
                          // Show upload indicator
                          setProcesses(prev => prev.map(p => 
                            p.id === process.id ? { ...p, isUploading: true } : p
                          ));

                          try {
                            // Create a temporary process entry for validation
                            const tempFormData = new FormData();
                            tempFormData.append('processFile', uploadFile);
                            tempFormData.append('projectId', projectId);
                            tempFormData.append('name', updatedProcess.name);
                            tempFormData.append('description', updatedProcess.description || '');

                            // Call new validation + create endpoint
                            const uploadResponse = await fetch('/api/processes/create-with-upload', {
                              method: 'POST',
                              body: tempFormData
                            });

                            const uploadData = await uploadResponse.json();

                            if (!uploadResponse.ok || !uploadData.success) {
                              // Validation failed - show error and don't create process
                              const errorMsg = uploadData.message || uploadData.error || 'Unknown error';
                              alert('Upload Failed\n\n' + errorMsg);
                              setProcesses(prev => prev.filter(p => p.id !== process.id));
                              return;
                            }

                            // Success - update process list with created process
                            const newProcess = uploadData.data.process;
                            setProcesses(prev => prev.map(p => 
                              p.id === process.id ? { ...newProcess, isEditing: false, isUploading: false } : p
                            ));
                            
                            // Notify that processes have changed
                            window.dispatchEvent(new CustomEvent('processesChanged', { detail: { projectId } }));
                            return;

                          } catch (uploadError) {
                            console.error('Upload validation failed:', uploadError);
                            alert('Upload failed: ' + (uploadError.message || 'Unknown error'));
                            setProcesses(prev => prev.filter(p => p.id !== process.id));
                            return;
                          }
                        }

                        // For repository type, create process first, then sync
                        const response = await processService.createProcess({
                          projectId,
                          name: updatedProcess.name,
                          description: updatedProcess.description,
                          repositoryUrl: updatedProcess.repositoryUrl || ''
                        });
                        const newProcess = response.data;
                        setProcesses(prev => prev.map(p => 
                          p.id === process.id ? { ...newProcess, isEditing: false, isSyncing: updatedProcess.sourceType === 'repository' } : p
                        ));
                        
                        // Notify that processes have changed
                        window.dispatchEvent(new CustomEvent('processesChanged', { detail: { projectId } }));

                        // Handle repository auto-sync
                        if (updatedProcess.sourceType === 'repository') {
                          // Auto-sync from repository
                          try {
                            const syncResponse = await fetch('/api/repository/sync', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                projectId,
                                processId: newProcess.id,
                                repoUrl: updatedProcess.repositoryUrl
                              })
                            });

                            const syncData = await syncResponse.json();

                            if (syncData.success) {
                              const updatedWithSync = {
                                ...newProcess,
                                technicalName: syncData.data.technicalName,
                                version: syncData.data.version,
                                lastSyncCommit: syncData.data.commitInfo.hash,
                                lastSyncDate: syncData.data.syncDate,
                                isSyncing: false
                              };
                              setProcesses(prev => prev.map(p => 
                                p.id === newProcess.id ? updatedWithSync : p
                              ));
                            } else {
                              setProcesses(prev => prev.map(p => 
                                p.id === newProcess.id ? { ...p, isSyncing: false } : p
                              ));
                            }
                          } catch (syncError) {
                            console.error('Auto-sync failed:', syncError);
                            setProcesses(prev => prev.map(p => 
                              p.id === newProcess.id ? { ...p, isSyncing: false } : p
                            ));
                          }
                        }
                      } catch (error) {
                        console.error('Error creating process:', error);
                        alert('Failed to create process: ' + error.message);
                      }
                    } else {
                      // Update existing process
                      try {
                        // Only send allowed fields to API (filter out read-only fields)
                        const updatePayload = {
                          name: updatedProcess.name,
                          description: updatedProcess.description,
                          repositoryUrl: updatedProcess.repositoryUrl
                        };
                        
                        // Include optional fields if they exist in updatedProcess
                        if (updatedProcess.technicalName !== undefined) {
                          updatePayload.technicalName = updatedProcess.technicalName;
                        }
                        if (updatedProcess.version !== undefined) {
                          updatePayload.version = updatedProcess.version;
                        }
                        if (updatedProcess.lastSyncCommit !== undefined) {
                          updatePayload.lastSyncCommit = updatedProcess.lastSyncCommit;
                        }
                        if (updatedProcess.lastSyncDate !== undefined) {
                          updatePayload.lastSyncDate = updatedProcess.lastSyncDate;
                        }
                        
                        // Update basic process info
                        await processService.updateProcess(process.id, updatePayload);
                        
                        // If there's a file upload for manual process, handle it
                        if (uploadFile && !process.repositoryUrl) {
                          setProcesses(prev => prev.map(p => 
                            p.id === process.id ? { ...p, isUploading: true, isEditing: false } : p
                          ));

                          try {
                            const formData = new FormData();
                            formData.append('processFile', uploadFile);

                            const uploadResponse = await fetch(`/api/processes/${process.id}/upload`, {
                              method: 'POST',
                              body: formData
                            });

                            const uploadData = await uploadResponse.json();

                            if (uploadData.success) {
                              // Reload the process to get updated metadata
                              const processResponse = await processService.getProcess(process.id);
                              const updatedProcessData = processResponse.data;
                              
                              setProcesses(prev => prev.map(p => 
                                p.id === process.id ? { ...updatedProcessData, isUploading: false } : p
                              ));
                            } else {
                              alert('Upload Failed\n\n' + (uploadData.message || 'Unknown error'));
                              setProcesses(prev => prev.map(p => 
                                p.id === process.id ? { ...p, isUploading: false } : p
                              ));
                            }
                          } catch (uploadError) {
                            console.error('Upload failed:', uploadError);
                            alert('Upload failed: ' + (uploadError.message || 'Unknown error'));
                            setProcesses(prev => prev.map(p => 
                              p.id === process.id ? { ...p, isUploading: false } : p
                            ));
                          }
                        } else {
                          // No upload, just update UI
                          setProcesses(prev => prev.map(p => 
                            p.id === process.id ? { ...p, ...updatedProcess, isEditing: false } : p
                          ));
                        }
                      } catch (error) {
                        console.error('Error updating process:', error);
                        alert('Failed to update process: ' + error.message);
                      }
                    }
                  }}
                  onDelete={async () => {
                    if (process.isNew) {
                      setProcesses(prev => prev.filter(p => p.id !== process.id));
                    } else {
                      handleDeleteProcessClick(process);
                    }
                  }}
                  onEdit={() => {
                    setProcesses(prev => prev.map(p => 
                      p.id === process.id ? { ...p, isEditing: true } : p
                    ));
                  }}
                  onCancel={() => {
                    if (process.isNew) {
                      setProcesses(prev => prev.filter(p => p.id !== process.id));
                    } else {
                      setProcesses(prev => prev.map(p => 
                        p.id === process.id ? { ...p, isEditing: false } : p
                      ));
                    }
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 border border-gray-200 border-dashed rounded-lg">
              <p className="text-sm text-gray-500">No processes added yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Add Process" to link a repository</p>
            </div>
          )}
          </>
          )}
        </div>

        {/* Project Documents Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div 
            className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 cursor-pointer"
            onClick={() => setIsDocumentsExpanded(!isDocumentsExpanded)}
          >
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 bg-purple-500 rounded mr-3"></div>
              <h3 className="text-lg font-semibold text-gray-800">Project Documents</h3>
              <span className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded-full font-medium text-center leading-tight">
                Coming<br/>Soon
              </span>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-500 transition-transform ${isDocumentsExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isDocumentsExpanded && (
          <>
          <div className="flex items-center justify-between mb-4">
            <div />
            <button 
              type="button" 
              onClick={(e) => {
                e.stopPropagation();
                addDocument();
              }}
              className="btn-secondary text-xs px-4 py-2"
            >
              + Add Document
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mb-4">
            Attach internal files or reference external URLs and provide a short description. Unlimited entries.
          </p>
          
          {documents.length > 0 ? (
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-gray-50">
              {documents.map(doc => (
                <DocumentItem 
                  key={doc.id}
                  document={doc}
                  onSave={saveDocument}
                  onEdit={editDocument}
                  onRemove={removeDocument}
                  onCancel={cancelEdit}
                />
              ))}
            </ul>
          ) : (
            <div className="text-center py-8 bg-gray-50 border border-gray-200 border-dashed rounded-lg">
              <p className="text-sm text-gray-500">No documents added yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Add Document" to attach files or links</p>
            </div>
          )}
          </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center space-x-3">
            <button 
              type="submit" 
              className="btn-primary px-8 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button 
              type="button" 
              onClick={handleReset} 
              className="btn-secondary px-8 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              Reset
            </button>
            {saveStatus && <span className="text-sm text-gray-600 ml-2">{saveStatus}</span>}
          </div>
          
          {/* Delete Project Button */}
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="px-6 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-colors"
          >
            Delete Project
          </button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Project</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this project? This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteProject}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Process Confirmation Modal */}
      <DeleteProcessModal
        isOpen={showDeleteProcessModal}
        processName={processToDelete?.name || ''}
        onConfirm={handleConfirmDeleteProcess}
        onCancel={handleCancelDeleteProcess}
      />

      {/* Add Process Disclaimer Modal */}
      <SyncDisclaimerModal
        isOpen={showAddProcessDisclaimerModal}
        onConfirm={() => {
          setShowAddProcessDisclaimerModal(false);
          // Add new process after confirmation
          const newProcess = {
            id: `temp-${Date.now()}`,
            name: '',
            description: '',
            repositoryUrl: '',
            isEditing: true,
            isNew: true
          };
          setProcesses(prev => [...prev, newProcess]);
        }}
        onCancel={() => {
          setShowAddProcessDisclaimerModal(false);
        }}
      />
    </div>
  );
};

// Process Item Component
const ProcessItem = ({ process, projectId, onUpdate, onDelete, onEdit, onCancel }) => {
  const [editData, setEditData] = useState({
    name: process.name || '',
    description: process.description || '',
    repositoryUrl: process.repositoryUrl || '',
    sourceType: process.repositoryUrl ? 'repository' : (process.isNew ? '' : 'manual') // 'repository' or 'manual'
  });
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [showSyncDisclaimerModal, setShowSyncDisclaimerModal] = useState(false);
  const [pendingSyncAction, setPendingSyncAction] = useState(null);

  const handleSave = () => {
    if (!editData.name.trim()) {
      alert('Process name is required');
      return;
    }
    
    // For new processes, validate based on source type
    if (process.isNew) {
      if (!editData.sourceType) {
        alert('Please select a source type (Repository URL or Manual Upload)');
        return;
      }
      if (editData.sourceType === 'repository' && !editData.repositoryUrl.trim()) {
        alert('Repository URL is required');
        return;
      }
      if (editData.sourceType === 'manual' && !uploadFile) {
        alert('Please select a file to upload');
        return;
      }
    } else {
      // For existing processes being edited, just validate repository URL if present
      if (editData.repositoryUrl && !editData.repositoryUrl.trim()) {
        alert('Repository URL cannot be empty');
        return;
      }
    }
    
    onUpdate({ ...editData, isNew: process.isNew }, uploadFile);
  };

  const handleSync = async () => {
    if (!editData.repositoryUrl.trim()) {
      setSyncStatus('❌ Please enter a repository URL');
      return;
    }

    // Show disclaimer modal before syncing
    setPendingSyncAction(() => performSync);
    setShowSyncDisclaimerModal(true);
  };

  const performSync = async () => {
    setSyncing(true);
    setSyncStatus('🔍 Syncing repository...');

    try {
      const response = await fetch('/api/repository/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectId,
          processId: process.id,
          repoUrl: editData.repositoryUrl
        })
      });

      const data = await response.json();

      if (data.success) {
        setSyncStatus(`✅ Successfully synced ${data.data.parsedFiles} workflow files!`);
        
        // Update process with sync info if not editing
        if (!process.isEditing) {
          onUpdate({
            ...process,
            technicalName: data.data.technicalName,
            version: data.data.version,
            lastSyncCommit: data.data.commitInfo.hash,
            lastSyncDate: data.data.syncDate // When the sync was performed
          });
        }
      } else {
        setSyncStatus(`❌ ${data.message || 'Sync failed'}`);
      }
    } catch (error) {
      setSyncStatus(`❌ Error: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setSyncStatus('❌ Please select a file to upload');
      return;
    }

    // Show disclaimer modal before uploading
    setPendingSyncAction(() => performUpload);
    setShowSyncDisclaimerModal(true);
  };

  const performUpload = async () => {
    setUploading(true);
    setSyncStatus('📤 Uploading and processing file...');

    try {
      const formData = new FormData();
      formData.append('processFile', uploadFile);

      const response = await fetch(`/api/processes/${process.id}/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSyncStatus(`✅ Successfully processed ${data.data.parsedFiles} workflow files!`);
        setUploadFile(null); // Clear file selection
        
        // Update process with metadata
        onUpdate({
          ...process,
          technicalName: data.data.technicalName,
          version: data.data.version,
          lastSyncCommit: null,
          lastSyncDate: data.data.syncDate
        });
      } else {
        const errorMsg = data.message || 'Upload failed';
        setSyncStatus(`❌ ${errorMsg}`);
        alert('Upload Failed\n\n' + errorMsg);
      }
    } catch (error) {
      setSyncStatus(`❌ Error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  if (process.isEditing) {
    return (
      <div className="relative p-4 border border-gray-300 rounded-lg bg-gray-50">
        {/* Uploading Overlay - Edit Mode */}
        {(uploading || process.isUploading) && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
              <p className="mt-3 text-sm font-medium text-green-600">Processing upload...</p>
              <p className="mt-1 text-xs text-gray-500">Extracting UiPath code</p>
            </div>
          </div>
        )}
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Process Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Dispatcher, Performer"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this process"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          
          {/* Source Type Selection (only for new processes) */}
          {process.isNew && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                Source Type <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sourceType"
                    value="repository"
                    checked={editData.sourceType === 'repository'}
                    onChange={(e) => setEditData(prev => ({ ...prev, sourceType: e.target.value }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Repository URL</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sourceType"
                    value="manual"
                    checked={editData.sourceType === 'manual'}
                    onChange={(e) => setEditData(prev => ({ ...prev, sourceType: e.target.value }))}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Manual Upload</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Repository URL field (shown for repository type or existing processes with URL) */}
          {(editData.sourceType === 'repository' || (!process.isNew && process.repositoryUrl)) && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Repository URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={editData.repositoryUrl}
                onChange={(e) => setEditData(prev => ({ ...prev, repositoryUrl: e.target.value }))}
                placeholder="https://bitbucket.org/elementblue/repo-name/src/branch/"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Format: https://bitbucket.org/workspace/repository/src/branch/
              </p>
            </div>
          )}
          
          {/* Manual Upload field (shown for manual type - new processes or existing manual processes) */}
          {(editData.sourceType === 'manual' || (!process.isNew && !process.repositoryUrl)) && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Upload File {process.isNew && <span className="text-red-500">*</span>}
              </label>
              <input
                type="file"
                accept=".zip,.nupkg"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {uploadFile && (
                <p className="text-xs text-gray-500 mt-1">
                  {uploadFile.name} - {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Accepted formats: .zip, .nupkg
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center space-x-2">
              <button 
                type="button" 
                onClick={handleSave}
                className="btn-primary px-4 py-2 text-sm"
              >
                {process.isNew ? 'Create' : 'Save'}
              </button>
              <button 
                type="button" 
                onClick={onCancel}
                className="btn-secondary px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
            {!process.isNew && (
              <button 
                type="button" 
                onClick={onDelete}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border border-gray-200 rounded-lg relative">
      {/* Syncing Overlay */}
      {process.isSyncing && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg z-10">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="mt-3 text-sm font-medium text-blue-600">Syncing repository...</p>
            <p className="mt-1 text-xs text-gray-500">Extracting UiPath code</p>
          </div>
        </div>
      )}
      
      {/* Uploading Overlay */}
      {(uploading || process.isUploading) && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-lg z-10">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
            <p className="mt-3 text-sm font-medium text-green-600">Processing upload...</p>
            <p className="mt-1 text-xs text-gray-500">Extracting UiPath code</p>
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {/* Title and Action Buttons Row */}
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">{process.name}</h4>
          
          <div className="flex items-center space-x-2">
            <button 
              type="button" 
              onClick={onEdit}
              className="btn-secondary px-4 py-2 text-xs"
            >
              Edit
            </button>
            
            {/* Show Sync button only if process has repository URL */}
            {process.repositoryUrl && (
              <button 
                type="button" 
                onClick={handleSync}
                disabled={syncing || uploading}
                className="btn-primary px-4 py-2 text-xs disabled:opacity-50"
              >
                {syncing ? 'Syncing...' : 'Sync'}
              </button>
            )}
          </div>
        </div>
        
        {/* Description */}
        {process.description && (
          <p className="text-xs text-gray-600">{process.description}</p>
        )}
        
        {/* Sync Status */}
        {syncStatus && (
          <span className="text-xs text-gray-700">{syncStatus}</span>
        )}
        
        {/* Unified Process Details Block */}
        <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
          <div className="space-y-2">
            {process.technicalName && (
              <div className="flex items-start">
                <span className="text-xs font-semibold text-gray-700 min-w-[140px]">UiPath Process Name:</span>
                <span className="text-xs text-gray-900 font-mono">{process.technicalName}</span>
              </div>
            )}
            {process.version && (
              <div className="flex items-start">
                <span className="text-xs font-semibold text-gray-700 min-w-[140px]">Process Version:</span>
                <span className="text-xs text-gray-900 font-mono">{process.version}</span>
              </div>
            )}
            {process.repositoryUrl && (
              <div className="flex items-start">
                <span className="text-xs font-semibold text-gray-700 min-w-[140px]">Repository:</span>
                <a 
                  href={process.repositoryUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-xs text-blue-600 hover:underline break-all"
                >
                  {process.repositoryUrl}
                </a>
              </div>
            )}
            {process.lastSyncDate && (
              <>
                <div className="flex items-start">
                  <span className="text-xs font-semibold text-gray-700 min-w-[140px]">Last Synced:</span>
                  <span className="text-xs text-gray-900">
                    {new Date(process.lastSyncDate).toLocaleString()}
                  </span>
                </div>
                {process.lastSyncCommit && (
                  <div className="flex items-start">
                    <span className="text-xs font-semibold text-gray-700 min-w-[140px]">Commit:</span>
                    <span className="text-xs text-gray-900 font-mono">{process.lastSyncCommit.substring(0, 7)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sync Disclaimer Modal */}
      <SyncDisclaimerModal
        isOpen={showSyncDisclaimerModal}
        onConfirm={() => {
          setShowSyncDisclaimerModal(false);
          if (pendingSyncAction) {
            pendingSyncAction();
            setPendingSyncAction(null);
          }
        }}
        onCancel={() => {
          setShowSyncDisclaimerModal(false);
          setPendingSyncAction(null);
          setSyncStatus('❌ Sync cancelled');
          setTimeout(() => setSyncStatus(''), 2000);
        }}
      />
    </div>
  );
};

// Document Item Component
const DocumentItem = ({ document, onSave, onEdit, onRemove, onCancel }) => {
  const [editData, setEditData] = useState({
    type: document.type || 'link',
    url: document.url || '',
    file: null,
    description: document.description || ''
  });

  const handleSave = () => {
    if (!editData.description.trim()) return;
    if (editData.type === 'link' && !editData.url.trim()) return;
    if (editData.type === 'file' && !editData.file && !document.fileName) return;
    
    onSave(document.id, editData);
  };

  if (document.isEditing) {
    return (
      <li className="p-4">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-1 text-xs font-medium">
              <input 
                type="radio" 
                name={`doc-type-${document.id}`} 
                value="link"
                checked={editData.type === 'link'}
                onChange={(e) => setEditData(prev => ({ ...prev, type: 'link' }))}
              />
              <span>Link</span>
            </label>
            <label className="flex items-center space-x-1 text-xs font-medium">
              <input 
                type="radio" 
                name={`doc-type-${document.id}`} 
                value="file"
                checked={editData.type === 'file'}
                onChange={(e) => setEditData(prev => ({ ...prev, type: 'file' }))}
              />
              <span>File</span>
            </label>
          </div>
          
          <div className="flex-1 space-y-2">
            {editData.type === 'link' ? (
              <input
                type="url"
                value={editData.url}
                onChange={(e) => setEditData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://docs.example.com/spec"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <input
                type="file"
                onChange={(e) => setEditData(prev => ({ ...prev, file: e.target.files[0] }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            <input
              type="text"
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Short description (e.g., Solution Design Spec)"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              type="button" 
              onClick={handleSave}
              className="btn-primary px-4 py-2 text-sm"
            >
              Save
            </button>
            <button 
              type="button" 
              onClick={() => onCancel(document.id)}
              className="btn-secondary px-4 py-2 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="p-4 flex flex-col md:flex-row md:items-start md:space-x-4 space-y-3 md:space-y-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">
          {document.type === 'link' ? (
            <a href={document.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">
              {document.url}
            </a>
          ) : (
            document.fileName || '(file)'
          )}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {document.description} • {document.type === 'link' ? 'Link' : 'File'}
        </p>
      </div>
      <div className="flex items-center space-x-2">
        <button 
          type="button" 
          onClick={() => onEdit(document.id)}
          className="btn-secondary px-4 py-2 text-xs"
        >
          Edit
        </button>
        <button 
          type="button" 
          onClick={() => onRemove(document.id)}
          className="btn-secondary px-4 py-2 text-xs"
        >
          Remove
        </button>
      </div>
    </li>
  );
};

// Delete Process Confirmation Modal Component
const DeleteProcessModal = ({ isOpen, processName, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-96">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Process</h3>
        <p className="text-sm text-gray-700 mb-6">
          Are you sure you want to delete the process <strong>"{processName}"</strong>?
          <br /><br />
          This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Sync Disclaimer Modal Component
const SyncDisclaimerModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-[500px]">
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Data Privacy Notice</h3>
          </div>
        </div>
        <div className="text-sm text-gray-700 mb-6 space-y-3">
          <p>
            <strong>Important:</strong> The process code you're about to sync will be analyzed using <strong>Google Gemini AI</strong>.
          </p>
          <p>
            Please ensure that your XAML workflow files and configuration files <strong>do not contain any sensitive information</strong>, including:
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Personal Identifiable Information (PII)</li>
            <li>Protected Health Information (PHI)</li>
            <li>Credentials, API keys, or passwords</li>
            <li>Confidential business data</li>
          </ul>
          <p className="text-xs text-gray-600 mt-3">
            By proceeding, you confirm that the code being uploaded is safe to analyze with third-party AI services.
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            I Understand, Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectConfigTab;